import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material'
import CopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { renderCitationText } from '../citations/CitationMarker'
import { createCitationNumbering } from '../../utils/citations'

interface Section {
  title: string
  content: string
  tablesUsed: string[]
  individualAnalyses?: Record<string, any>
  metadata?: {
    type: string
    description: string
    tablesCount: number
    successfulAnalyses: number
  }
}

interface ExecutivePillarEnhancedProps {
  title: string
  content: string
  sections?: Section[]
  onCopy: (content: string) => void
  onEdit: (title: string, content: string) => void
  loadTableInModal: (tableName: string) => void
  onOpenAnalysesModal?: (section: Section) => void
}

export const ExecutivePillarEnhanced: React.FC<ExecutivePillarEnhancedProps> = ({ 
  title, 
  content, 
  sections,
  onCopy, 
  onEdit, 
  loadTableInModal,
  onOpenAnalysesModal
}) => {
  const renderMarkdownContent = (text: string, _forceInlineStyle = false) => {
    // #1296: citations render as numbered markers with the table name on hover,
    // through the SAME shared renderer the Gen AI cards use. One numbering per
    // rendered block, in first-use order.
    const numbering = createCitationNumbering(text)

    const processChildren = (children: any): any => {
      if (typeof children === 'string') {
        return renderCitationText(
          children,
          numbering,
          (reference) => loadTableInModal(reference.label),
        )
      }

      // Handle React elements and arrays
      if (Array.isArray(children)) {
        return children.flatMap((child: any) => {
          if (React.isValidElement(child)) {
            // If it's a React element, check if it has string children we need to process
            const childProps = child.props as any
            if (typeof childProps?.children === 'string') {
              return React.cloneElement(child, {
                ...childProps,
                children: processChildren(childProps.children)
              })
            }
            return child
          }
          return processChildren(child)
        })
      }
      
      return children
    }

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => {
            const text = React.Children.toArray(children).join('')
            // Hide citation headers
            if (text.toLowerCase().includes('citation') || text === 'Table Citations') {
              return null
            }
            return (
              <Typography variant="h6" sx={{ mt: 2, mb: 1.5, fontWeight: 600 }}>
                {children}
              </Typography>
            )
          },
          h3: ({ children }) => (
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          p: ({ children }) => (
            <Typography component="div" variant="body2" sx={{ mb: 1.5, lineHeight: 1.6, '&:last-child': { mb: 0 } }}>
              {processChildren(children)}
            </Typography>
          ),
          ul: ({ children }) => {
            // Check if this is a citations list by looking at content
            const childrenArray = React.Children.toArray(children)
            // Only hide if ALL children are pure table citations (not content with inline citations)
            const isPureCitationsList = childrenArray.length > 0 && childrenArray.every(child => {
              const text = React.isValidElement(child) ? 
                React.Children.toArray((child.props as any)?.children).join('') : 
                String(child)
              // Check if this is just a table citation (starts with [Table: and ends with ])
              return text.trim().match(/^\[Table:.*\]$/)
            })
            
            if (isPureCitationsList) {
              // Hide pure citation lists at the bottom
              return null
            }
            
            return (
              <Box component="ul" sx={{ 
                pl: 2, 
                mb: 1.5, 
                mt: 0, 
                '&:last-child': { mb: 0 },
                listStyleType: 'disc',
                '& ul': {
                  listStyleType: 'circle',
                  pl: 2,
                  mb: 0.5,
                  mt: 0.5
                }
              }}>
                {children}
              </Box>
            )
          },
          ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 2, mb: 1.5, mt: 0, '&:last-child': { mb: 0 } }}>
              {children}
            </Box>
          ),
          li: ({ children }) => {
            // Check if this is a citation list item (only hide if it's JUST a table reference)
            const text = React.Children.toArray(children).join('')
            
            // Debug log for any list item that contains "Sky's brand attributes"
            if (text.includes("Sky's brand attributes")) {
              console.log('PROSPECTS LIST ITEM DEBUG:')
              console.log('Text:', text)
              console.log('Regex match:', text.trim().match(/^\[Table:.*\]$/))
              console.log('Will render:', !text.trim().match(/^\[Table:.*\]$/))
            }
            
            // Only hide if the line is primarily just a table citation (e.g., starts with [Table:)
            if (text.trim().match(/^\[Table:.*\]$/)) {
              // Hide pure citation list items
              return null
            }
            
            return (
              <Typography component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6, display: 'list-item' }}>
                {processChildren(children)}
              </Typography>
            )
          },
          strong: ({ children }) => (
            <Typography component="span" variant="body2" sx={{ fontWeight: 'bold' }}>
              {processChildren(children)}
            </Typography>
          ),
          em: ({ children }) => (
            <Typography component="span" variant="body2" sx={{ fontStyle: 'italic' }}>
              {processChildren(children)}
            </Typography>
          ),
          a: ({ href, children }) => (
            <Link href={href} color="primary" target="_blank" rel="noopener">
              {children}
            </Link>
          ),
          blockquote: ({ children }) => (
            <Box 
              sx={{ 
                borderLeft: '3px solid',
                borderLeftColor: 'warning.main',
                pl: 2,
                py: 0.5,
                mb: 1.5,
                bgcolor: 'warning.light',
                borderRadius: '0 4px 4px 0'
              }}
            >
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'warning.contrastText' }}>
                {children}
              </Typography>
            </Box>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    )
  }

  return (
    <Card 
      sx={{ 
        height: '100%',
        position: 'relative',
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
        '&:hover': {
          boxShadow: 3
        }
      }}
    >
      <CardContent sx={{ p: 3, pb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              fontSize: '1.1rem'
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* <Tooltip title="Edit section">
              <IconButton 
                size="small" 
                onClick={() => onEdit(title, content)}
                sx={{ 
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Copy to clipboard">
              <IconButton 
                size="small" 
                onClick={() => onCopy(content)}
                sx={{ 
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Main pillar summary content - but hide citation sections */}
        {(() => {
          // Filter out citation sections from the content
          const contentLines = content.split('\n')
          const filteredLines: string[] = []
          let inCitationSection = false
          
          contentLines.forEach((line) => {
            // Check if this starts a citation section
            if (line.match(/^#{2,3}.*([Cc]itations?|Table Citations)/)) {
              inCitationSection = true
              return
            }
            // Check if we're starting a new non-citation section
            if (line.match(/^#{2,3}/) && !line.match(/[Cc]itations?/)) {
              inCitationSection = false
            }
            // Only include lines if not in citation section
            if (!inCitationSection) {
              filteredLines.push(line)
            }
          })
          
          const filteredContent = filteredLines.join('\n').trim()
          return renderMarkdownContent(filteredContent)
        })()}
        
        {/* Sections with content or data analyses */}
        {sections && sections.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {sections.map((section, index) => {
              // Render all sections regardless of content length
              console.log(`Rendering section ${section.title} with content length: ${section.content?.length || 0}`)
              console.log(`First 200 chars: ${section.content?.substring(0, 200) || 'No content'}`)
              
              // Debug log for Prospects Analysis specifically
              if (section.title === 'Prospects Analysis') {
                console.log('PROSPECTS ANALYSIS DEBUG:')
                console.log('Content exists:', !!section.content)
                console.log('Content type:', typeof section.content)
                console.log('Full content:', section.content)
              }
              
              return (
                <Box key={index} sx={{ mb: 3 }}>
                  {/* Section content - no title, just flow */}
                  {section.content && (
                    <Box sx={{ 
                      '& > *:not(:last-child)': { mb: 1.5 },
                      '& ul': { mt: 0, mb: 0 },
                      '& li': { mb: 0.5 }
                    }}>
                      {renderMarkdownContent(section.content)}
                    </Box>
                  )}
                  {/* Analysis link for this section */}
                  {section.individualAnalyses && onOpenAnalysesModal && (
                    <Box 
                      sx={{ 
                        mt: 1.5,
                        mb: 2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        opacity: 0.7,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={() => onOpenAnalysesModal(section)}
                    >
                      <AnalyticsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="caption" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                        View {section.tablesUsed?.length || 0} detailed table analyses
                      </Typography>
                    </Box>
                  )}
                </Box>
              )
            })}

          </Box>
        )}
      </CardContent>
    </Card>
  )
}